import React, { useState, useCallback } from 'react'
import { EditableCell } from './EditableCell'
import { EditableDropdown } from './EditableDropdown'
import { ColumnHeader } from './ColumnHeader'
import { useUpdateBoQItem, useDeleteBoQItem, useCreateBoQItem } from '../../hooks/useBoQ'
import { LoadingSkeleton } from '../common/LoadingSkeleton'

interface BoQTableProps {
  projectId: number
  categories: any[]
  isLoading?: boolean
  onRefresh?: () => void
}

export const BoQTable: React.FC<BoQTableProps> = ({
  projectId,
  categories,
  isLoading = false,
  onRefresh
}) => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    itemNumber: 60,
    itemName: 280,
    specification: 180,
    unit: 70,
    quantity: 100,
    unitPrice: 140,
    totalPrice: 140,
    actions: 80
  })

  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set()
  )

  // Update expanded categories when categories change
  React.useEffect(() => {
    if (categories.length > 0) {
      setExpandedCategories(new Set(categories.map((c) => c.id)))
    }
  }, [categories])

  const updateItemMutation = useUpdateBoQItem(projectId)
  const deleteItemMutation = useDeleteBoQItem(projectId)
  const createItemMutation = useCreateBoQItem(projectId)

  // Format currency for display
  const formatCurrency = (value: number | undefined) => {
    if (!value) return 0
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value)
  }

  // Handle column width change
  const handleColumnResize = useCallback((columnId: string, newWidth: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnId]: newWidth
    }))
  }, [])

  // Handle category expand/collapse
  const toggleCategory = useCallback((categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId)
      } else {
        newSet.add(categoryId)
      }
      return newSet
    })
  }, [])

  // Handle item update
  const handleUpdateItem = useCallback(
    (itemId: number, field: string, value: any) => {
      updateItemMutation.mutate({
        itemId,
        data: { [field]: value }
      })
    },
    [updateItemMutation]
  )

  // Handle item deletion
  const handleDeleteItem = useCallback(
    (itemId: number, itemName: string) => {
      if (confirm(`Delete "${itemName}"? This cannot be undone.`)) {
        deleteItemMutation.mutate(itemId)
      }
    },
    [deleteItemMutation]
  )

  // Handle add new item
  const handleAddItem = useCallback(
    (categoryId: number) => {
      const newItem = {
        categoryId,
        itemName: 'New Item',
        unit: 'm2',
        quantity: 0
      }
      createItemMutation.mutate(newItem)
    },
    [createItemMutation]
  )

  return (
    <div className="card overflow-hidden">
      {/* Header info */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex gap-8">
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Items</span>
              <span className="text-2xl font-bold text-slate-900 mt-1">
                {categories.reduce((sum, cat) => sum + cat.itemCount, 0)}
              </span>
            </div>
            <div className="w-px h-12 bg-slate-200" />
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Value</span>
              <span className="text-2xl font-bold text-primary-600 mt-1">
                {formatCurrency(
                  categories.reduce((sum, cat) => sum + cat.totalValue, 0)
                )}
              </span>
            </div>
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="btn-secondary"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                  Refreshing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full border-collapse bg-white table-fixed">
          {/* Table Header */}
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 shadow-sm">
            <tr>
              <th style={{ width: 40 }} className="p-0 border-r border-slate-200 bg-slate-50" />
              <th style={{ width: columnWidths.itemNumber }} className="p-0 m-0 border-none"><ColumnHeader label="No." width={columnWidths.itemNumber} onResize={(w) => handleColumnResize('itemNumber', w)} /></th>
              <th style={{ width: columnWidths.itemName }} className="p-0 m-0 border-none"><ColumnHeader label="Item Pekerjaan" width={columnWidths.itemName} onResize={(w) => handleColumnResize('itemName', w)} /></th>
              <th style={{ width: columnWidths.specification }} className="p-0 m-0 border-none"><ColumnHeader label="Spesifikasi" width={columnWidths.specification} onResize={(w) => handleColumnResize('specification', w)} /></th>
              <th style={{ width: columnWidths.unit }} className="p-0 m-0 border-none"><ColumnHeader label="Sat." width={columnWidths.unit} onResize={(w) => handleColumnResize('unit', w)} /></th>
              <th style={{ width: columnWidths.quantity }} className="p-0 m-0 border-none"><ColumnHeader label="Qty" width={columnWidths.quantity} onResize={(w) => handleColumnResize('quantity', w)} /></th>
              <th style={{ width: columnWidths.unitPrice }} className="p-0 m-0 border-none"><ColumnHeader label="Harga Satuan" width={columnWidths.unitPrice} onResize={(w) => handleColumnResize('unitPrice', w)} /></th>
              <th style={{ width: columnWidths.totalPrice }} className="p-0 m-0 border-none"><ColumnHeader label="Jumlah Harga" width={columnWidths.totalPrice} onResize={(w) => handleColumnResize('totalPrice', w)} /></th>
              <th style={{ width: columnWidths.actions }} className="p-0 m-0 border-none"><ColumnHeader label="Actions" width={columnWidths.actions} onResize={(w) => handleColumnResize('actions', w)} /></th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-slate-100">
            {categories.map((category) => (
              <React.Fragment key={category.id}>
                {/* Category Row */}
                <tr className="bg-slate-50/80 hover:bg-slate-100 transition-colors group">
                  <td className="px-2 py-3 border-r border-slate-200 text-center">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="text-slate-400 hover:text-primary-600 transition-colors w-6 h-6 flex items-center justify-center rounded hover:bg-white"
                    >
                      {expandedCategories.has(category.id) ? '▼' : '▶'}
                    </button>
                  </td>
                  <td
                    style={{ width: columnWidths.itemNumber }}
                    className="px-3 py-3 font-bold text-slate-800 border-r border-slate-200"
                  >
                    {category.code}
                  </td>
                  <td
                    style={{ width: columnWidths.itemName }}
                    className="px-3 py-3 font-bold text-slate-800 border-r border-slate-200"
                  >
                    {category.name}
                  </td>
                  <td
                    style={{ width: columnWidths.specification }}
                    className="px-3 py-3 text-slate-500 border-r border-slate-200 italic"
                  >
                    —
                  </td>
                  <td
                    style={{ width: columnWidths.unit }}
                    className="px-3 py-3 text-right text-slate-500 border-r border-slate-200"
                  >
                    —
                  </td>
                  <td
                    style={{ width: columnWidths.quantity }}
                    className="px-3 py-3 text-right text-slate-600 border-r border-slate-200 font-medium tabular-nums"
                  >
                    {category.totalQty?.toLocaleString('id-ID') || 0}
                  </td>
                  <td
                    style={{ width: columnWidths.unitPrice }}
                    className="px-3 py-3 text-right text-slate-500 border-r border-slate-200"
                  >
                    —
                  </td>
                  <td
                    style={{ width: columnWidths.totalPrice }}
                    className="px-3 py-3 text-right font-semibold text-slate-900 border-r border-slate-200 tabular-nums"
                  >
                    {formatCurrency(category.totalValue)}
                  </td>
                  <td
                    style={{ width: columnWidths.actions }}
                    className="px-2 py-3 text-center"
                  >
                    <button
                      onClick={() => handleAddItem(category.id)}
                      className="text-xs inline-flex items-center px-2 py-1 bg-white border border-green-200 text-green-700 rounded hover:bg-green-50 hover:border-green-300 transition-all shadow-sm opacity-0 group-hover:opacity-100"
                      title="Add new item"
                    >
                      + Item
                    </button>
                  </td>
                </tr>

                {/* Item Rows */}
                {expandedCategories.has(category.id) &&
                  category.items?.map((item: any, idx: number) => (
                    <tr key={item.id} className="hover:bg-blue-50/30 transition-colors group">
                      <td className="px-2 py-1.5 border-r border-slate-200 text-center text-xs text-slate-400">
                        {/* Dot indicator for alignment */}
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mx-auto" />
                      </td>
                      <td
                        style={{ width: columnWidths.itemNumber }}
                        className="px-2 py-1.5 text-sm text-slate-600 border-r border-slate-200 font-medium"
                      >
                        {item.itemNumber || idx + 1}
                      </td>
                      <td style={{ width: columnWidths.itemName }} className="px-2 py-1.5 border-r border-slate-200">
                        <EditableCell
                          value={item.itemName}
                          onChange={(val) => handleUpdateItem(item.id, 'itemName', val)}
                          isLoading={updateItemMutation.isPending}
                        />
                      </td>
                      <td style={{ width: columnWidths.specification }} className="px-2 py-1.5 border-r border-slate-200">
                        <EditableCell
                          value={item.specification as unknown as string}
                          onChange={(val) => handleUpdateItem(item.id, 'specification', val)}
                          isLoading={updateItemMutation.isPending}
                          placeholder="—"
                        />
                      </td>
                      <td style={{ width: columnWidths.unit }} className="px-2 py-1.5 border-r border-slate-200">
                        <EditableDropdown
                          value={item.unit}
                          options={['m2', 'm3', 'phn', 'krg', 'm', 'ls', 'plb', 'unit', 'bh']}
                          onChange={(val) => handleUpdateItem(item.id, 'unit', val)}
                          isLoading={updateItemMutation.isPending}
                        />
                      </td>
                      <td style={{ width: columnWidths.quantity }} className="px-2 py-1.5 border-r border-slate-200">
                        <EditableCell
                          type="number"
                          value={item.quantity}
                          onChange={(val) => handleUpdateItem(item.id, 'quantity', val)}
                          isLoading={updateItemMutation.isPending}
                        />
                      </td>
                      <td style={{ width: columnWidths.unitPrice }} className="px-2 py-1.5 border-r border-slate-200">
                        <EditableCell
                          type="currency"
                          value={item.unitPrice}
                          onChange={(val) => handleUpdateItem(item.id, 'unitPrice', val)}
                          isLoading={updateItemMutation.isPending}
                        />
                      </td>
                      <td
                        style={{ width: columnWidths.totalPrice }}
                        className="px-2 py-1.5 border-r border-slate-200 text-right font-medium text-slate-700 tabular-nums"
                      >
                        {formatCurrency(item.totalPrice)}
                      </td>
                      <td
                        style={{ width: columnWidths.actions }}
                        className="px-2 py-1.5 text-center"
                      >
                        <button
                          onClick={() => handleDeleteItem(item.id, item.itemName)}
                          disabled={deleteItemMutation.isPending}
                          className="btn-danger opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete item"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Loading state - Overlay when refreshing but data exists */}
      {isLoading && categories.length > 0 && (
        <div className="absolute inset-0 bg-white/50 z-20 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="mt-2 text-sm font-medium text-primary-600">Syncing...</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && categories.length === 0 && (
        <div className="p-16 text-center">
          <div className="mx-auto h-12 w-12 text-slate-300 mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-lg font-medium text-slate-900">No BoQ categories found</p>
          <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">
            Get started by creating a new category using the button above.
          </p>
        </div>
      )}
    </div>
  )
}

